import { createContext, useContext, useEffect, useRef, useState } from 'react';

import Tooltip from '@/app/_components/common/tooltip/Tooltip';

type showTooltipProps = (
  content: string,
  targetElement: HTMLElement,
  tooltipPos?: 'left' | 'mid' | 'right',
) => void;

interface TooltipContextProps {
  showTooltip: showTooltipProps;
  hideTooltip: () => void;
}

const TooltipContext = createContext<TooltipContextProps | null>(null);

export const useTooltip = () => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltip must be used within a TooltipProvider');
  }

  return context;
};

export const TooltipProvider = ({ children }: { children: React.ReactNode }) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<{
    content: string;
    transform: string;
  } | null>(null);

  const [tooltipTarget, setTooltipTarget] = useState<null | HTMLElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState<string>('');
  const [tooltipContent, setTooltipContent] = useState<string>('');
  const showTooltip = (
    content: string,
    targetElement: HTMLElement | null,
    tooltipPos: 'left' | 'mid' | 'right' = 'mid',
  ) => {
    setTooltipTarget(targetElement);
    setTooltipPosition(tooltipPos);
    setTooltipContent(content);

    // 툴팁을 초기화

    setTooltip({
      content: content,
      transform: '', // 초기에는 빈 값으로 설정
    });
  };

  const calculateTooltipPosition = () => {
    if (tooltipTarget && tooltipRef.current) {
      const rect = tooltipTarget.getBoundingClientRect();
      const tooltipRect = tooltipRef.current?.getBoundingClientRect();
      const windowRect = document.body.getBoundingClientRect();
      // console.log('window:', windowRect, ' \ntargetRect:', rect, ' \ntooltipRect:', tooltipRect);
      let position = {
        x:
          tooltipPosition === 'mid'
            ? rect.left + rect.width / 2 - tooltipRect.width / 2 // 가운데 정렬
            : tooltipPosition === 'left'
              ? rect.left + rect.width / 4
              : rect.right + 8,
        y: rect.bottom + 8, // 툴팁이 타겟 위쪽에 표시
      };

      // 툴팁이 화면을 벗어나지 않도록 위치 조정
      if (position.x < windowRect.left) {
        position.x = windowRect.left + 10; // 최소 좌측 여백 설정
      } else if (position.x + tooltipRect.width > windowRect.right) {
        position.x = windowRect.right - tooltipRect.width - 10; // 우측 여백 설정
      }

      if (position.y + tooltipRect.height > windowRect.bottom) {
        position.y = rect.top - tooltipRect.height - 10; // 툴팁이 하단을 벗어날 경우 위쪽에 표시
      }

      // 최종 위치 설정
      tooltipRef.current.style.transform = `translate3d(${position.x}px, ${position.y}px, 0)`;
    }
  };

  useEffect(() => {
    if (tooltipTarget && tooltipPosition && tooltipRef.current) {
      calculateTooltipPosition();
    }
  }, [tooltip, tooltipPosition, tooltipContent]);

  useEffect(() => {
    if (tooltip) {
      const handleResize = () => {
        calculateTooltipPosition();
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [tooltip]);

  const hideTooltip = () => {
    setTooltip(null);
  };

  return (
    <TooltipContext.Provider value={{ showTooltip, hideTooltip }}>
      {children}
      {tooltip && (
        <Tooltip content={tooltip.content} transform={tooltip.transform} ref={tooltipRef} />
      )}
    </TooltipContext.Provider>
  );
};
